// src/JudgeGongsSequential.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { LiveStation, RoundShooter, LiveRound, StationResult, GongConfig, JudgeShotsResult } from "./types/liveScoring";
import { unlockAudio, speak } from "./utils/audio";
import { useShotTimer } from "./judge/useShotTimer";
import { liveSaveErrorMessage } from "./utils/liveResults";

interface Props {
  station: LiveStation;
  currentRound: LiveRound | null;
  currentShooter: RoundShooter | null;
  currentShooterIndex: number;
  existingResult?: StationResult;
  onResultSaved: (result: JudgeShotsResult) => Promise<void>;
  draftShots?: number[];
  onDraftChange?: (shots: number[]) => void;
  /**
   * Hláška o neuloženém výsledku tohoto střelce od rodiče – přežije
   * přemountování (u posledního střelce rundy rodič během ukládání ukáže
   * „Runda hotová“ a po zamítnutí gongy vrátí).
   */
  saveError?: string | null;
}

const JudgeGongsSequential: React.FC<Props> = ({
  station, currentRound, currentShooter, currentShooterIndex,
  existingResult, onResultSaved, draftShots, onDraftChange, saveError,
}) => {
  const gongs: GongConfig[] = (station.config as any).gongs || [];
  const totalShots = station.shotsCount;
  const timeLimit = station.timeLimit;

  const initShots = draftShots && draftShots.length > 0 ? draftShots
    : (existingResult?.shots && existingResult.shots.length > 0 ? existingResult.shots : []);
  const [shots, setShots]         = useState<number[]>(initShots);
  const [phase, setPhase]         = useState<"shooting" | "summary">(
    initShots.length >= totalShots ? "summary" : "shooting"
  );
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving]       = useState(false);
  // Zamítnuté uložení (návrh 3.6) – hláška nad tlačítkem, rány zůstanou
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [shotVoice, setShotVoice] = useState(true);
  const touchedRef = useRef(false);
  // Časomíra (fáze 3, G1): pauza a pokračování, podle koncového času,
  // zrušitelný odpočet i hlášky
  const timer = useShotTimer(timeLimit, soundEnabled);
  const timerSecs = timer.secs;

  // Reset při změně střelce NEBO při změně draftShots (např. po převzetí ve dual módu)
  const draftKey = draftShots?.join(",") ?? "";
  useEffect(() => {
    const init = draftShots && draftShots.length > 0 ? draftShots
      : (existingResult?.shots && existingResult.shots.length > 0 ? existingResult.shots : []);
    setShots(init);
    setPhase(init.length >= totalShots ? "summary" : "shooting");
    setIsEditing(false);
    setSaving(false);
    setConfirmError(null);
    timer.reset();
  // Timer resetujeme jen při skutečné změně střelce, ne při každé ráně
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShooter?.startNumber, timeLimit]); // draftKey záměrně vynecháno aby timer nepadal

  // Sleduj existingResult async (onSnapshot) + draftShots změnu (dual pickup)
  useEffect(() => {
    const src = draftShots && draftShots.length > 0 ? draftShots
      : existingResult?.shots?.length ? existingResult.shots : null;
    if (!src) return;
    setShots(prev => {
      if (prev.length > 0) return prev;
      return src;
    });
    setPhase(prev => prev === "summary" ? prev :
      (src.length >= totalShots ? "summary" : "shooting"));
    // Jen při novém uloženém výsledku nebo převzetí draftu (draftKey = obsah
    // draftShots); rány ani časomíra efekt spouštět nesmí
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingResult?.score, draftKey]);

  // Ruční start a „Čekat → Start“ – odemknutí zvuku z gesta (Nesmí: onTouchStart unlock)
  const startManual = () => {
    unlockAudio();
    timer.start();
  };
  const startAutoSequence = () => {
    unlockAudio();
    timer.startAuto();
  };

  const currentGongIndex = shots.length;
  const currentGong = gongs[currentGongIndex] || gongs[gongs.length - 1];
  const isLastShot = shots.length === totalShots - 1;
  const isDone = shots.length >= totalShots;

  const recordShot = useCallback((hit: boolean) => {
    if (isDone || saving) return;
    const val = hit ? 1 : 0;
    const newShots = [...shots, val];
    setShots(newShots);
    onDraftChange?.(newShots);
    if (newShots.length >= totalShots) {
      setPhase("summary");
      // Timer pokračuje i po posledním zásahu
    }
  }, [shots, isDone, saving, totalShots, onDraftChange]);

  const undoLastShot = () => {
    if (shots.length === 0 || saving) return;
    const newShots = shots.slice(0, -1);
    setShots(newShots);
    onDraftChange?.(newShots);
    setPhase("shooting");
  };

  const computeScore = (arr: number[]) =>
    Math.round(arr.reduce((s, hit, i) => s + (hit ? (gongs[i] || gongs[gongs.length-1]).value : 0), 0) * 100) / 100;

  const handleConfirm = async () => {
    if (saving || !currentShooter) return;
    setSaving(true);
    setConfirmError(null);
    // Čas zadání a rozhodčího doplní zápis (saveStationResult)
    const result: JudgeShotsResult = {
      score: computeScore(shots), shots,
      gongValues: gongs.map(g => g.value),
    };
    let saved = false;
    try {
      await onResultSaved(result);
      saved = true;
    } catch (err) {
      // Zápis zamítnut (závod ukončen, opravil hlavní rozhodčí, nejste
      // v seznamu rozhodčích…): rány zůstanou, zpět na potvrzení s hláškou
      setConfirmError(liveSaveErrorMessage(err));
      setPhase("summary");
    } finally {
      // Nikdy nezamrznout na „Ukládám…“
      setSaving(false);
    }
    if (!saved) return;
    setShots([]);
    setPhase("shooting");
    setIsEditing(false);
  };

  // Hláška rodiče má přednost – je přesnější (např. smazané stanoviště, F1)
  const errorText = saveError || confirmError || null;

  const timerColor = timerSecs <= 10 ? "#dc3545" : timerSecs <= 20 ? "#fd7e14" : "#198754";
  const activeShooters = currentRound?.shooters.filter(s => s.active) || [];
  const partialScore = computeScore(shots);

  if (!currentRound || !currentShooter) return null;

  return (
    // Třídy lks-judge-gongs* platí jen pro telefon na šířku (App.css, @media
    // landscape + max-height 500px): karta střelce a stav ran vlevo,
    // ZÁSAH/NULA vpravo přes celou výšku. Na výšku a na tabletu bez pravidel.
    <div className={`d-flex flex-column lks-judge-gongs lks-judge-gongs--seq lks-judge-gongs--${phase}`} style={{ flex: 1, minHeight: 0, overflow: "hidden", userSelect: "none", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      onTouchStart={unlockAudio}>

      {/* Karta střelce a stav ran: na šířku levý sloupec, který se posouvá sám
          (App.css); jinak display: contents – rozložení přesně jako dřív */}
      <div className="lks-judge-gongs__side">
      {/* ── Hlavička střelce ── */}
      <div className="px-3 pt-3 pb-2 lks-judge-gongs__head">
        <div className="card bg-dark text-white shadow">
          <div className="card-body py-2 px-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-bold" style={{ fontSize: "1.2rem" }}>
                  #{currentShooter.startNumber} {currentShooter.name}
                </div>
                <div className="text-white-50 small">
                  Runda {currentRound.number} · Střelec {currentShooterIndex + 1} / {activeShooters.length}
                  {currentShooter.sequenceNumber && <span className="ms-2 opacity-75">LKS #{currentShooter.sequenceNumber}</span>}
                </div>
              </div>
              {timeLimit && (
                <div className="text-end">
                  {timer.phase === "countdown" ? (
                    <div style={{ color: "#ffc107", fontSize: "1.3rem", fontWeight: 700 }}>⏳ čekat…</div>
                  ) : timer.phase === "idle" ? (
                    <div className="d-flex gap-1">
                      <button className="btn btn-success btn-sm" onClick={startManual}>
                        <i className="bi bi-play-fill me-1" />Start
                      </button>
                      <button className="btn btn-outline-warning btn-sm lks-on-dark-warning" onClick={startAutoSequence}>
                        <i className="bi bi-hourglass me-1" />Čekat→Start
                      </button>
                    </div>
                  ) : timer.phase === "paused" ? (
                    <div className="d-flex flex-column align-items-end gap-1">
                      <div style={{ color: "#fd7e14", fontSize: "1.5rem", fontWeight: 700 }}>⏸ {timerSecs}s</div>
                      <button className="btn btn-warning btn-sm" onClick={timer.resume}>
                        <i className="bi bi-play-fill me-1" />Pokračovat
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ color: timerColor, fontSize: "1.8rem", fontWeight: 700 }}>{timerSecs}s</div>
                      <button className="btn btn-outline-warning btn-sm py-0 px-2 lks-on-dark-warning" onClick={timer.pause} title="Pauza">
                        <i className="bi bi-pause-fill" />
                      </button>
                    </div>
                  )}
                  <button className="btn btn-link btn-sm text-white-50 p-0 mt-1" onClick={() => setSoundEnabled(v => !v)}>
                    <i className={`bi bi-volume-${soundEnabled ? "up" : "mute"}`} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {phase === "shooting" && (
          <div className="px-3 pt-2 pb-1 text-center lks-judge-gongs__info" style={{ flexShrink: 0 }}>
            <div className="lks-judge-gongs__count" style={{ fontSize: "1.1rem", color: "#555", marginBottom: "0.25rem" }}>
              GONG <strong style={{ fontSize: "1.4rem", color: "#004d40" }}>{shots.length + 1}</strong>
              {" / "}{totalShots}
              {currentGong && <span style={{ marginLeft: "0.6rem", color: "#004d40", fontWeight: 700, fontSize: "1.3rem" }}>· {currentGong.value} b.</span>}
              {isLastShot && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "0.7rem", verticalAlign: "middle" }}>poslední</span>}
            </div>
            <div className="lks-judge-gongs__shots" style={{ display: "flex", alignItems: "center", gap: "0.3rem", overflowX: "auto",
              overflowY: "hidden", justifyContent: "center", minHeight: 28, paddingBottom: "0.1rem" }}>
              <div style={{ display: "flex", gap: "0.3rem", flexShrink: 0 }}>
                {shots.map((s, i) => (
                  <span key={i} className={`badge ${s ? "bg-success" : "bg-danger"}`} style={{ fontSize: "0.72rem", flexShrink: 0 }}>
                    G{i+1}: {s ? `${gongs[i]?.value}b` : "0"}
                  </span>
                ))}
                <span className="badge" style={{ background: "#444", fontSize: "0.72rem", flexShrink: 0 }}>Σ{partialScore}b</span>
              </div>
              <button className="btn btn-outline-secondary btn-sm py-0 px-2"
                style={{ flexShrink: 0, fontSize: "0.75rem", opacity: shots.length > 0 ? 1 : 0.3 }}
                onClick={undoLastShot} disabled={shots.length === 0} title="Vrátit poslední ránu">
                <i className="bi bi-arrow-counterclockwise" />
              </button>
            </div>
          </div>
      )}
      </div>

      {/* ── FÁZE: Střelba ── */}
      {phase === "shooting" && (
        <div className="lks-judge-gongs__shoot" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div className="px-3 pb-1 lks-judge-gongs__voice">
            <button className={`btn btn-sm w-100 ${shotVoice ? "btn-outline-success" : "btn-outline-secondary"}`}
              style={{ fontSize: "0.75rem" }} onClick={() => setShotVoice(v => !v)}>
              <i className={`bi bi-chat-${shotVoice ? "text" : "slash"} me-1`} />
              {shotVoice ? "Hlasení rán: ZAP" : "Hlasení rán: VYP"}
            </button>
          </div>
          {/* Mezera okrajem, ne flex gap – Chrome 80 ho nezná (fáze 3, I6) */}
          <div className="lks-judge-gongs__hits" style={{ flex: 1, minHeight: 0, display: "flex", padding: "0.25rem 1rem 0.75rem" }}>
            <button className="btn btn-success flex-fill"
              style={{ fontSize: "clamp(1.3rem, 3.5vw, 2rem)", borderRadius: 14, minHeight: 0, overflow: "hidden", marginRight: "0.6rem" }}
              onTouchStart={() => { touchedRef.current = true; if (shotVoice && !saving) speak("Zásah"); }}
              onMouseDown={() => { if (!touchedRef.current && shotVoice && !saving) speak("Zásah"); touchedRef.current = false; }}
              onClick={() => recordShot(true)} disabled={saving}>
              ✅ ZÁSAH
            </button>
            <button className="btn btn-danger flex-fill"
              style={{ fontSize: "clamp(1.3rem, 3.5vw, 2rem)", borderRadius: 14, minHeight: 0, overflow: "hidden" }}
              onTouchStart={() => { touchedRef.current = true; if (shotVoice && !saving) speak("Nula"); }}
              onMouseDown={() => { if (!touchedRef.current && shotVoice && !saving) speak("Nula"); touchedRef.current = false; }}
              onClick={() => recordShot(false)} disabled={saving}>
              ❌ NULA
            </button>
          </div>
        </div>
      )}

      {/* ── FÁZE: Souhrn ── */}
      {phase === "summary" && (
        <div className="px-3 pt-2 lks-judge-gongs__summary" style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}>
          {/* Score + edit toggle */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <span style={{ fontSize: "2rem", fontWeight: 800, color: "#198754" }}>{computeScore(shots)}</span>
              <span className="text-muted ms-1">b.</span>
              <span className="text-muted ms-2" style={{ fontSize: "0.85rem" }}>
                ({shots.filter(s => s===1).length}/{totalShots} zásahů)
              </span>
            </div>
            <button
              className={`btn btn-sm ${isEditing ? "btn-warning" : "btn-outline-secondary"}`}
              onClick={() => setIsEditing(v => !v)} disabled={saving}>
              <i className={`bi bi-${isEditing ? "lock-fill" : "pencil"} me-1`} />
              {isEditing ? "Zamknout" : "Opravit"}
            </button>
          </div>

          {/* Gongy – zamčené nebo editovatelné */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            {shots.map((s, i) => {
              const g = gongs[i] || gongs[gongs.length-1];
              if (!isEditing) {
                return (
                  <span key={i} className={`badge ${s ? "bg-success" : "bg-danger"}`} style={{ fontSize: "0.85rem" }}>
                    G{i+1}: {s ? `✅ ${g.value}b` : "❌ 0"}
                  </span>
                );
              }
              return (
                <div key={i} className="d-flex align-items-center gap-1 border rounded px-2 py-1"
                  style={{ background: "#f8f9fa" }}>
                  <span className="text-muted" style={{ fontSize: "0.75rem", minWidth: 24 }}>G{i+1}</span>
                  <button
                    className={`btn btn-sm py-0 px-2 ${s===1 ? "btn-success" : "btn-outline-success"}`}
                    style={{ fontSize: "0.78rem" }}
                    onClick={() => { const n=[...shots]; n[i]=1; setShots(n); onDraftChange?.(n); }}>
                    ✅ {g.value}b
                  </button>
                  <button
                    className={`btn btn-sm py-0 px-2 ${s===0 ? "btn-danger" : "btn-outline-danger"}`}
                    style={{ fontSize: "0.78rem" }}
                    onClick={() => { const n=[...shots]; n[i]=0; setShots(n); onDraftChange?.(n); }}>
                    ❌ 0
                  </button>
                </div>
              );
            })}
          </div>

          {/* Hláška a potvrzení: na šířku přilepené dole v souhrnu (vždy vidět),
              jinak display: contents – rozložení jako dřív */}
          <div className="lks-judge-gongs__actions">
            {errorText && !saving && (
              <div className="alert alert-danger small py-2 px-3 mt-3 mb-0" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-1" />{errorText}
              </div>
            )}
            <button className="btn btn-success mt-3 mb-1" onClick={handleConfirm} disabled={saving}>
              {saving
                ? <><span className="spinner-border spinner-border-sm me-2" />Ukládám...</>
                : <><i className="bi bi-check-circle me-1" />Potvrdit a další</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JudgeGongsSequential;
